/**
 * Main Application Component for Backoffice
 * @implements US-099 - Backoffice Dashboard View
 * @implements FS-001.2.1 - OAuth Authentication
 *
 * Sets up the application with required providers:
 * - ApolloProvider for GraphQL
 * - ThemeProvider for Material-UI
 * - RouterProvider for routing
 *
 * Shows login page when not authenticated (OAuth in production, dev auth in development).
 */

import { ApolloProvider } from '@apollo/client';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { RouterProvider, BrowserRouter, Routes, Route } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';

import { apolloClient } from '@/apollo';
import { theme } from '@/theme';
import { router } from '@/routes';
import { useAuth } from '@/hooks';
import { LoginPage } from '@/pages';

/**
 * Inner app that waits for auth to complete before rendering routes.
 * Shows login page when not authenticated instead of error message.
 * @implements FS-001.2.1 - OAuth Authentication
 */
function AppContent() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Not authenticated - show login page
  // Uses BrowserRouter for login page since it's outside the main router
  if (!isAuthenticated) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="*" element={<LoginPage />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // Authenticated - show main app routes
  return <RouterProvider router={router} />;
}

/**
 * Root App component with providers.
 * @implements US-099 - Backoffice Dashboard View
 * @implements FS-001.2.1 - OAuth Authentication
 */
function App() {
  return (
    <ApolloProvider client={apolloClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppContent />
      </ThemeProvider>
    </ApolloProvider>
  );
}

export default App;
